import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './blog.scss';
import Seo, { SITE_URL } from '../components/seo/Seo';
import LeadForm from '../components/lead-form';
import { findPost, getPost, postPath, PUBLISHED } from '../content/blog';
import { findService, getServiceContent, servicePath } from '../content/services';
import { localeFromPath } from '../lib/i18n-routes';

/** Rasm yo'q bo'lsa blokni yashiramiz — buzilgan ikonka ko'rinmasin */
function SafeImage({ image, className, priority = false }) {
    const [broken, setBroken] = useState(false);
    if (!image || broken) return null;

    return (
        <figure className={className}>
            <img
                src={image.src}
                alt={image.alt}
                width="1400"
                height="875"
                loading={priority ? 'eager' : 'lazy'}
                fetchpriority={priority ? 'high' : undefined}
                decoding={priority ? 'sync' : 'async'}
                onError={() => setBroken(true)}
            />
            {image.caption ? <figcaption>{image.caption}</figcaption> : null}
        </figure>
    );
}

/** Maqola tanasi — bloklardan semantik HTML quriladi */
function Body({ blocks, images }) {
    return blocks.map((b, i) => {
        switch (b.type) {
            case 'h2':
                return <h2 key={i} className="post-h2">{b.text}</h2>;
            case 'h3':
                return <h3 key={i} className="post-h3">{b.text}</h3>;
            case 'p':
                return <p key={i}>{b.text}</p>;
            case 'list':
                return (
                    <ul key={i} className="post-list">
                        {b.items.map((it, n) => <li key={n}><span>+</span>{it}</li>)}
                    </ul>
                );
            case 'ol':
                return (
                    <ol key={i} className="post-ol">
                        {b.items.map((it, n) => <li key={n}>{it}</li>)}
                    </ol>
                );
            case 'quote':
                return <blockquote key={i} className="post-quote">{b.text}</blockquote>;
            case 'callout':
                return <aside key={i} className="post-callout">{b.text}</aside>;
            case 'table':
                return (
                    <div key={i} className="post-table-wrap">
                        <table className="post-table">
                            <thead>
                                <tr>{b.head.map((h, n) => <th key={n}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {b.rows.map((row, n) => (
                                    <tr key={n}>{row.map((cell, m) => <td key={m}>{cell}</td>)}</tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            case 'image':
                return <SafeImage key={i} image={images?.[b.image]} className="post-figure" />;
            default:
                return null;
        }
    });
}

function BlogPost() {
    const { slug } = useParams();
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const lang = localeFromPath(pathname);

    const post = findPost(slug);
    const c = post ? getPost(post.slug, lang) : null;

    useEffect(() => { window.scrollTo(0, 0); }, [slug]);

    const services = useMemo(() => {
        if (!c?.relatedServices) return [];
        return c.relatedServices
            .map((s) => findService(s))
            .filter(Boolean)
            .map((s) => ({ ...s, content: getServiceContent(s.slug, lang) }))
            .filter((s) => s.content);
    }, [c, lang]);

    const posts = useMemo(() => {
        if (!c?.relatedPosts) return [];
        return c.relatedPosts
            .map((s) => PUBLISHED.find((p) => p.slug === s))
            .filter(Boolean)
            .map((p) => ({ ...p, content: getPost(p.slug, lang) }))
            .filter((p) => p.content);
    }, [c, lang]);

    if (!post || !c) return <Navigate to="/404" replace />;

    const path = postPath(post, lang);
    const altPath = postPath(post, lang === 'ru' ? 'uz' : 'ru');
    const blogRoot = lang === 'ru' ? '/ru/blog' : '/blog';
    const hero = c.images?.[0];

    const schema = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'BlogPosting',
                '@id': `${SITE_URL}${path}#article`,
                headline: c.h1,
                description: c.meta.description,
                image: hero ? `${SITE_URL}${hero.src}` : undefined,
                datePublished: post.date,
                dateModified: post.date,
                inLanguage: lang,
                author: { '@type': 'Organization', name: 'Innobek', url: SITE_URL },
                publisher: { '@id': `${SITE_URL}/#organization` },
                mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${path}` },
            },
            {
                '@type': 'BreadcrumbList',
                itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'Innobek', item: SITE_URL },
                    { '@type': 'ListItem', position: 2, name: t('news'), item: `${SITE_URL}${blogRoot}` },
                    { '@type': 'ListItem', position: 3, name: c.h1, item: `${SITE_URL}${path}` },
                ],
            },
            ...(c.faq?.length ? [{
                '@type': 'FAQPage',
                mainEntity: c.faq.map((f) => ({
                    '@type': 'Question',
                    name: f.q,
                    acceptedAnswer: { '@type': 'Answer', text: f.a },
                })),
            }] : []),
        ],
    };

    return (
        <main className="blog-post">
            <Seo
                title={c.meta.title}
                description={c.meta.description}
                keywords={c.meta.keywords}
                lang={lang}
                path={path}
                altPath={altPath}
                image={hero?.src}
                type="article"
                schema={schema}
            />

            <article className="post-article">
                <div className="container">
                    <nav className="svc-crumbs" aria-label="breadcrumb">
                        <Link to={lang === 'ru' ? '/ru' : '/'}>Innobek</Link>
                        <span aria-hidden="true">/</span>
                        <Link to={blogRoot}>{t('news')}</Link>
                        <span aria-hidden="true">/</span>
                        <span>{c.h1}</span>
                    </nav>

                    <header className="post-head">
                        <p className="h8 sub-title">{c.category}</p>
                        <h1 className="post-title">{c.h1}</h1>
                        <p className="post-lead">{c.lead}</p>
                        {c.readingTime ? (
                            <p className="post-meta">{c.readingTime}</p>
                        ) : null}
                    </header>

                    <SafeImage image={hero} className="post-hero" priority />

                    <div className="post-body">
                        <Body blocks={c.body} images={c.images} />
                    </div>

                    {c.faq?.length ? (
                        <section className="post-faq">
                            <h2 className="post-h2">{t('faq')}</h2>
                            <dl>
                                {c.faq.map((f) => (
                                    <div key={f.q}>
                                        <dt>{f.q}</dt>
                                        <dd>{f.a}</dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    ) : null}

                    {services.length ? (
                        <section className="post-related">
                            <h2 className="post-h2">{t('related-services')}</h2>
                            <div className="post-related__grid">
                                {services.map((s) => (
                                    <Link className="svc-related__card" key={s.slug} to={servicePath(s, lang)}>
                                        <h3>{s.content.hero.h1}</h3>
                                        <p>{s.content.definition?.text || s.content.meta.description}</p>
                                        <span className="svc-related__more">{t('read-more')} &#8599;</span>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ) : null}

                    <section className="post-cta" id="ariza">
                        <div className="post-cta__grid">
                            <div>
                                <h2 className="post-h2">{c.cta.h}</h2>
                                <p>{c.cta.p}</p>
                            </div>
                            <LeadForm source={c.h1} submitLabel={c.cta.button} variant="compact" />
                        </div>
                    </section>

                    {posts.length ? (
                        <section className="post-related">
                            <h2 className="post-h2">{t('useful-info')}</h2>
                            <div className="post-related__grid">
                                {posts.map((p) => (
                                    <Link className="svc-related__card" key={p.slug} to={postPath(p, lang)}>
                                        <h3>{p.content.h1}</h3>
                                        <p>{p.content.lead}</p>
                                        <span className="svc-related__more">{t('read-more')} &#8599;</span>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ) : null}
                </div>
            </article>
        </main>
    );
}

export default BlogPost;
